'use client';

import { Avatar, Badge, Button, cn } from '@heroui/react';
import { useClipboard } from '@heroui/use-clipboard';
import { Icon } from '@iconify/react';
import React, { useEffect, useRef } from 'react';
import './loading.css';

export type MessageCardProps = React.HTMLAttributes<HTMLDivElement> & {
  avatar?: string;
  showFeedback?: boolean;
  message?: React.ReactNode;
  currentAttempt?: number;
  status?: 'success' | 'failed' | 'loading';
  attempts?: number;
  messageClassName?: string;
  onMessageCopy?: (content: string | string[]) => void;
  isReversed: boolean;
  isLast: boolean;
};

const MessageCard = React.forwardRef<HTMLDivElement, MessageCardProps>(
  (
    {
      avatar,
      message,
      showFeedback,
      attempts = 1,
      currentAttempt = 1,
      status,
      onMessageCopy,
      className,
      messageClassName,
      isReversed,
      isLast,
      ...props
    },
    ref,
  ) => {
    const messageRef = React.useRef<HTMLDivElement>(null);
    const { copied, copy } = useClipboard();
    const hasFailed = status === 'failed';
    const isLoading = status === 'loading';

    const failedMessageClassName =
      status === 'failed' ? 'bg-danger-100/50 border border-danger-100 text-foreground' : '';
    const failedMessage = <p>Something went wrong, i'm not able to answer right now</p>;
    const loadingMessage = (
      <div id="wave">
        <span className="dot"></span>
        <span className="dot"></span>
        <span className="dot"></span>
      </div>
    );

    const handleCopy = React.useCallback(() => {
      let stringValue = '';

      if (typeof message === 'string') {
        stringValue = message;
      } else if (Array.isArray(message)) {
        message.forEach((child) => {
          // @ts-ignore
          const childString = typeof child === 'string' ? child : child?.props?.children?.toString();

          if (childString) {
            stringValue += childString + '\n';
          }
        });
      }

      const valueToCopy = stringValue || messageRef.current?.textContent || '';

      copy(valueToCopy);

      onMessageCopy?.(valueToCopy);
    }, [copy, message, onMessageCopy]);

    useEffect(() => {
      if (isLast && !isReversed && status === 'success') {
        typeCharacter();
      } else if (streamingTextElement.current) {
        streamingTextElement.current.textContent = message?.toString() ?? '';
      }
    }, [status, isLast, isReversed]);

    const currentIndex = useRef(0);
    const streamingTextElement = useRef<HTMLDivElement>(null);

    function typeCharacter() {
      if (typeof message === 'string') {
        if ((streamingTextElement.current && message && currentIndex.current < message.length) || 0) {
          streamingTextElement.current!.textContent += message.charAt(currentIndex.current);
          currentIndex.current++;
          setTimeout(typeCharacter, 10); // Adjust the typing speed by changing the delay (in milliseconds)
        }
      }
    }

    return (
      <div
        {...props}
        ref={ref}
        className={cn(
          'flex gap-3 scroll-mt-[20px]',
          !isReversed && 'pb-6',
          isReversed && 'flex-row-reverse ml-auto max-w-[70%]',
          className,
          'group',
        )}
      >
        {avatar && (
          <div className="relative flex-none">
            <Badge
              isOneChar
              color="danger"
              content={<Icon className="text-background" icon="gravity-ui:circle-exclamation-fill" />}
              isInvisible={!hasFailed}
              placement="bottom-right"
              shape="circle"
            >
              <Avatar src={avatar} />
            </Badge>
          </div>
        )}
        <div className="flex flex-col gap-4">
          <div
            className={cn(
              'relative w-full rounded-medium px-1 py-2 text-default-600 min-w-min',
              failedMessageClassName,
              messageClassName,
              isReversed && 'bg-content2 px-4 py-3',
            )}
          >
            <div ref={messageRef} className={cn('pr-2 text-small whitespace-pre-wrap')}>
              {isLoading && loadingMessage}
              {hasFailed ? failedMessage : <div ref={streamingTextElement}></div>}
            </div>
            {showFeedback && !hasFailed && !isLoading && (
              <div
                className={cn(
                  'absolute flex rounded-full group-hover:opacity-100 transition-opacity',
                  !isReversed && '-bottom-8 left-0',
                  !isLast && 'opacity-0',
                )}
              >
                <Button isIconOnly radius="full" size="sm" variant="light" onPress={handleCopy}>
                  {copied ? (
                    <Icon className="text-lg text-default-600" icon="gravity-ui:check" />
                  ) : (
                    <Icon className="text-lg text-default-600" icon="gravity-ui:copy" />
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default MessageCard;

MessageCard.displayName = 'MessageCard';
